import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppNavbar from '../components/AppNavbar';
import AnimatedCount from '../components/AnimatedCount';
import CompetitionCard from '../components/CompetitionCard';
import HorizontalScroll from '../components/HorizontalScroll';
import VideoPreview from '../components/VideoPreview';
import { useAuth } from '../context/AuthContext';
import competitionService from '../services/competitionService';
import './HomePage.css';

const ACTIVE_COMPETITION_LIMIT = 12;
const RECENT_SUBMISSION_LIMIT = 6;

const HomePage = () => {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();
  const [activeCompetitions, setActiveCompetitions] = useState([]);
  const [recentSubmissions, setRecentSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let isMounted = true;

    const loadHomePage = async () => {
      setLoading(true);
      setError('');

      try {
        const competitionResponse = await competitionService.getAll({
          status: 'active',
          limit: ACTIVE_COMPETITION_LIMIT,
          offset: 0
        });
        const competitions = Array.isArray(competitionResponse?.competitions)
          ? competitionResponse.competitions
          : [];

        const submissionResponses = await Promise.all(
          competitions.map(async (competition) => {
            try {
              const response = await competitionService.getVisibleSubmissions(competition.id, {
                limit: RECENT_SUBMISSION_LIMIT,
                offset: 0
              });

              const curatedSubmissions = Array.isArray(response?.curatedSubmissions)
                ? response.curatedSubmissions
                : [];

              return curatedSubmissions.map((submission) => ({
                ...submission,
                competitionId: competition.id,
                competitionName: competition.name,
                competitionHashtag: competition.requiredHashtag
              }));
            } catch (submissionError) {
              return [];
            }
          })
        );

        if (!isMounted) {
          return;
        }

        setActiveCompetitions(competitions);
        setRecentSubmissions(
          submissionResponses
            .flat()
            .sort((left, right) => Number(right.submittedAt || 0) - Number(left.submittedAt || 0))
            .slice(0, RECENT_SUBMISSION_LIMIT)
        );
      } catch (loadError) {
        if (!isMounted) {
          return;
        }

        setError('Unable to load the latest creator activity right now.');
        setActiveCompetitions([]);
        setRecentSubmissions([]);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadHomePage();

    return () => {
      isMounted = false;
    };
  }, []);

  const navigationItems = isAuthenticated
    ? [
        { label: 'Home', to: '/', isActive: true },
        { label: 'Competitions', to: '/competitions', isActive: false },
        { label: 'Dashboard', to: '/dashboard', isActive: false }
      ]
    : [
        { label: 'Home', to: '/', isActive: true },
        { label: 'Login', to: '/login', isActive: false },
        { label: 'Sign Up', to: '/register', isActive: false }
      ];

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const activitySignals = useMemo(() => {
    const totalParticipants = activeCompetitions.reduce((sum, competition) => {
      return sum + Number(competition.participantCount || 0);
    }, 0);
    const totalSubmissions = activeCompetitions.reduce((sum, competition) => {
      return sum + Number(competition.totalSubmissions || 0);
    }, 0);
    const rewardPools = activeCompetitions.reduce((sum, competition) => {
      return sum + (Array.isArray(competition.prizes) && competition.prizes.length > 0 ? 1 : 0);
    }, 0);

    return [
      totalParticipants > 0 ? `${totalParticipants}+ creators are joining now` : 'Creators are joining now',
      totalSubmissions > 0 ? `${totalSubmissions}+ new submissions coming in` : 'New submissions coming in',
      rewardPools > 0 ? `${rewardPools} reward pools are live` : 'Rewards being distributed',
      recentSubmissions.length > 0 ? 'Fresh creator content is landing right now' : 'New creative opportunities are opening daily'
    ];
  }, [activeCompetitions, recentSubmissions.length]);

  const growthStats = useMemo(() => {
    const totalParticipants = activeCompetitions.reduce((sum, competition) => {
      return sum + Number(competition.participantCount || 0);
    }, 0);
    const totalSubmissions = activeCompetitions.reduce((sum, competition) => {
      return sum + Number(competition.totalSubmissions || 0);
    }, 0);
    const rewardsLive = activeCompetitions.reduce((sum, competition) => {
      return sum + (Array.isArray(competition.prizes) ? competition.prizes.length : 0);
    }, 0);

    const cards = [];
    if (totalParticipants > 0) {
      cards.push({ value: totalParticipants, suffix: '+', label: 'Creators participating' });
    }
    if (totalSubmissions > 0) {
      cards.push({ value: totalSubmissions, suffix: '+', label: 'Submissions shared' });
    }
    if (rewardsLive > 0) {
      cards.push({ value: rewardsLive, suffix: '+', label: 'Rewards live right now' });
    }

    return cards;
  }, [activeCompetitions]);

  return (
    <div className="home-page">
      <AppNavbar
        user={user}
        onLogout={isAuthenticated ? handleLogout : undefined}
        navigationItems={navigationItems}
        brandTo="/"
        showUserSection={isAuthenticated}
      />

      <main className="home-main">
        <section className="home-hero">
          <div className="home-hero-copy">
            <span className="home-hero-eyebrow">Creator growth platform</span>
            <h1>Grow your channel with creator challenges</h1>
            <p>Use trending sounds, create reels, and win rewards</p>
            <div className="home-hero-actions">
              <button
                type="button"
                className="home-primary-button"
                onClick={() => navigate(isAuthenticated ? '/competitions' : '/register')}
              >
                {isAuthenticated ? 'Start Creating' : 'Join Reelo'}
              </button>
              <button
                type="button"
                className="home-secondary-button"
                onClick={() => navigate('/competitions')}
              >
                Explore Challenges
              </button>
            </div>
          </div>

          <div className="home-hero-panel">
            <div className="home-hero-highlight">
              <strong>Creator momentum</strong>
              <p>Find active sounds, participate early, and turn every submission into channel growth.</p>
            </div>
            <div className="home-hero-metrics">
              <div>
                <span>{activeCompetitions.length > 0 ? activeCompetitions.length : 'Fresh'}</span>
                <small>active challenges</small>
              </div>
              <div>
                <span>{recentSubmissions.length > 0 ? recentSubmissions.length : 'Live'}</span>
                <small>creator previews</small>
              </div>
            </div>
          </div>
        </section>

        <ActivityBar items={activitySignals} />

        <section className="home-section">
          <SectionHeader
            title="🔥 Active Challenges"
            subtitle="Jump into the highest-opportunity challenges and start creating while momentum is still building."
            actionLabel="View All"
            onAction={() => navigate('/competitions')}
          />

          {loading ? (
            <HorizontalScroll
              className="home-horizontal-scroll"
              itemClassName="home-horizontal-scroll-item"
              ariaLabel="Active challenge placeholders"
              itemsPerView={{ mobile: 1, tablet: 2, desktop: 3 }}
            >
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="home-skeleton-card" />
              ))}
            </HorizontalScroll>
          ) : activeCompetitions.length > 0 ? (
            <HorizontalScroll
              className="home-horizontal-scroll"
              itemClassName="home-horizontal-scroll-item"
              ariaLabel="Active challenges"
              itemsPerView={{ mobile: 1, tablet: 2, desktop: 3 }}
            >
              {activeCompetitions.map((competition) => (
                <CompetitionCard
                  key={competition.id}
                  competition={competition}
                  onClick={(competitionId) => navigate(isAuthenticated ? `/competitions/${competitionId}` : '/login')}
                  onParticipate={(competitionId) => navigate(isAuthenticated ? `/competitions/${competitionId}?openSubmit=1` : '/login')}
                  onViewMediaSources={(competitionId) => navigate(isAuthenticated ? `/reels/${competitionId}` : '/login')}
                  onViewSubmissions={(competitionId) => navigate(isAuthenticated ? `/competitions/${competitionId}/submissions` : '/login')}
                  showFullActions={false}
                >
                  <div className="home-card-actions">
                    {competition.status === 'active' && (
                      <button
                        type="button"
                        className="home-inline-action primary"
                        onClick={(event) => {
                          event.stopPropagation();
                          navigate(isAuthenticated ? `/competitions/${competition.id}?openSubmit=1` : '/login');
                        }}
                      >
                        {isAuthenticated ? 'Participate' : 'Sign In to Join'}
                      </button>
                    )}
                    <button
                      type="button"
                      className="home-inline-action"
                      onClick={(event) => {
                        event.stopPropagation();
                        navigate(`/competitions/${competition.id}`);
                      }}
                    >
                      View Details
                    </button>
                  </div>
                </CompetitionCard>
              ))}
            </HorizontalScroll>
          ) : (
            <EmptyState
              title="New opportunities are on the way"
              description={error || 'Fresh challenges will appear here as soon as they go live.'}
              actionLabel="Browse Competitions"
              onAction={() => navigate('/competitions')}
            />
          )}
        </section>

        <section className="home-section">
          <SectionHeader
            title="🎥 See how creators are using this sound"
            subtitle="Preview recent creator submissions and jump straight into the reels feed for more inspiration."
          />

          {loading ? (
            <HorizontalScroll
              className="home-horizontal-scroll home-submission-scroll"
              itemClassName="home-horizontal-scroll-item"
              ariaLabel="Submission placeholders"
              itemsPerView={{ mobile: 1, tablet: 2, desktop: 3 }}
            >
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="home-skeleton-card compact" />
              ))}
            </HorizontalScroll>
          ) : recentSubmissions.length > 0 ? (
            <HorizontalScroll
              className="home-horizontal-scroll home-submission-scroll"
              itemClassName="home-horizontal-scroll-item"
              ariaLabel="Recent creator submissions"
              itemsPerView={{ mobile: 1, tablet: 2, desktop: 3 }}
              advanceBy="single"
            >
              {recentSubmissions.map((submission, index) => (
                <article
                  key={submission.id || `${submission.competitionId}-${index}`}
                  className="submission-preview-card"
                  onClick={() => navigate(`/reels/${submission.competitionId}`)}
                >
                  <VideoPreview
                    videoUrl={submission.videoUrl}
                    showTitle={false}
                    title={`${submission.competitionName} preview`}
                    className="submission-preview-video"
                  />
                  <div className="submission-preview-body">
                    <strong>{submission.competitionName}</strong>
                    <span>{formatHashtag(submission.competitionHashtag)}</span>
                    <small>{formatSubmissionTime(submission.submittedAt)}</small>
                  </div>
                </article>
              ))}
            </HorizontalScroll>
          ) : (
            <EmptyState
              title="Creator previews will show up here"
              description="Once curated submissions start flowing in, this section will spotlight them instantly."
              actionLabel="Open Reels"
              onAction={() => navigate('/reels')}
            />
          )}
        </section>

        <section className="home-section home-growth-section">
          <SectionHeader
            title="💰 Creators are growing on Reelo"
            subtitle="Momentum compounds when you show up early, use the sound well, and submit consistently."
          />

          {growthStats.length > 0 ? (
            <div className="growth-stats-grid">
              {growthStats.map((stat) => (
                <div key={stat.label} className="growth-stat-card">
                  <strong>
                    <AnimatedCount value={stat.value} suffix={stat.suffix || ''} duration={1400} />
                  </strong>
                  <span>{stat.label}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="growth-soft-copy">
              Creators are joining and growing every day.
            </div>
          )}
        </section>

        <section className="home-section">
          <SectionHeader
            title="How it works"
            subtitle="A simple loop built for creators who want momentum, proof, and consistency."
          />

          <div className="how-it-works-grid">
            <HowItWorksStep emoji="🎵" title="Pick a challenge" description="Find an active challenge with a sound and reward structure that fits your style." />
            <HowItWorksStep emoji="🎬" title="Create using the sound" description="Turn the source into a reel, short, or video that feels native to your audience." />
            <HowItWorksStep emoji="🚀" title="Submit and grow" description="Publish, submit, and build momentum as your channel gains exposure through active campaigns." />
          </div>
        </section>

        <section className="home-final-cta">
          <div>
            <span className="home-hero-eyebrow">Your next creator push starts here</span>
            <h2>Ready to grow your channel?</h2>
            <p>Join a live challenge, create with intent, and turn your next reel into a growth moment.</p>
          </div>
          <button type="button" className="home-primary-button" onClick={() => navigate('/competitions')}>
            {isAuthenticated ? 'Join Your First Challenge' : 'Create Your Account'}
          </button>
        </section>
      </main>
    </div>
  );
};

const ActivityBar = ({ items }) => {
  return (
    <section className="activity-bar">
      {items.map((item) => (
        <span key={item} className="activity-pill">{item}</span>
      ))}
    </section>
  );
};

const SectionHeader = ({ title, subtitle, actionLabel, onAction }) => {
  return (
    <div className="section-header">
      <div>
        <h2>{title}</h2>
        {subtitle && <p>{subtitle}</p>}
      </div>
      {actionLabel && typeof onAction === 'function' && (
        <button type="button" className="section-header-link" onClick={onAction}>
          {actionLabel}
        </button>
      )}
    </div>
  );
};

const EmptyState = ({ title, description, actionLabel, onAction }) => {
  return (
    <div className="home-empty-state">
      <h3>{title}</h3>
      <p>{description}</p>
      {actionLabel && typeof onAction === 'function' && (
        <button type="button" className="home-secondary-button" onClick={onAction}>
          {actionLabel}
        </button>
      )}
    </div>
  );
};

const HowItWorksStep = ({ emoji, title, description }) => {
  return (
    <article className="how-it-works-card">
      <span className="how-it-works-emoji">{emoji}</span>
      <h3>{title}</h3>
      <p>{description}</p>
    </article>
  );
};

function formatHashtag(hashtag) {
  if (!hashtag) {
    return '#challenge';
  }

  return hashtag.startsWith('#') ? hashtag : `#${hashtag}`;
}

function formatSubmissionTime(timestamp) {
  if (!timestamp) {
    return 'Fresh from the feed';
  }

  return new Date(timestamp).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric'
  });
}

export default HomePage;